import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-gray-800 rounded-lg shadow-xl p-8">
          <div className="mb-8">
            <button
              onClick={() => navigate(-1)}
              className="text-blue-400 hover:text-blue-300 mb-4"
            >
              ← Back
            </button>
            <h1 className="text-4xl font-bold text-white mb-4">Terms of Service</h1>
            <p className="text-gray-400">Last Updated: January 24, 2025</p>
          </div>

          <div className="prose prose-invert max-w-none">
            <div className="text-gray-300 space-y-6">
              
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">1. Acceptance of Terms</h2>
                <p>
                  By accessing and using TriviaPlug ("the Platform"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">2. Description of Service</h2>
                <p>
                  TriviaPlug is a platform that enables users to create, purchase, and host trivia games. The Platform provides:
                </p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Game creation tools for building trivia games</li>
                  <li>A marketplace for buying and selling trivia games</li>
                  <li>Launch rights for hosting games</li>
                  <li>Revenue sharing capabilities for game creators</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">3. User Accounts</h2>
                <p>Users must:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Provide accurate and complete information when creating an account</li>
                  <li>Maintain the security of their account credentials</li>
                  <li>Notify us immediately of any unauthorized use</li>
                  <li>Be responsible for all activities that occur under their account</li>
                  <li>Be at least 18 years old or have parental consent</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">4. Payments and Pricing</h2>
                <h3 className="text-xl font-semibold text-white mt-4 mb-2">4.1 Created Games</h3>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Game creation is free</li>
                  <li>Launching your created games costs $15 per launch</li>
                  <li>Each launch includes a 12-hour window</li>
                </ul>

                <h3 className="text-xl font-semibold text-white mt-4 mb-2">4.2 Marketplace Games</h3>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Marketplace games have a minimum base price of $20</li>
                  <li>A $15 launch fee is included in all marketplace purchases</li>
                  <li>Total checkout price = Base Price + $15 Launch Fee</li>
                  <li>Payments are processed through Stripe</li>
                </ul>

                <h3 className="text-xl font-semibold text-white mt-4 mb-2">4.3 Revenue Sharing</h3>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Creators receive 50% of the base game price</li>
                  <li>TriviaPlug receives 50% of the base price + 100% of the launch fee</li>
                  <li>Payouts to creators are handled via Stripe Connect</li>
                  <li>Revenue is distributed automatically after each sale</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">5. Refund Policy</h2>
                <h3 className="text-xl font-semibold text-white mt-4 mb-2">5.1 Created Game Launches</h3>
                <p>
                  Launch rights for created games are non-refundable once purchased, except in cases of technical failures or errors on our part.
                </p>

                <h3 className="text-xl font-semibold text-white mt-4 mb-2">5.2 Marketplace Games</h3>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Refunds may be requested within 24 hours of purchase if the game has not been launched</li>
                  <li>Refunds are at TriviaPlug's discretion for technical issues</li>
                  <li>Once a game has been launched, no refunds will be issued</li>
                  <li>Refund requests must be submitted through support channels</li>
                </ul>

                <h3 className="text-xl font-semibold text-white mt-4 mb-2">5.3 Processing</h3>
                <p>
                  Approved refunds will be processed within 5-10 business days to the original payment method.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">6. Game Content</h2>
                <h3 className="text-xl font-semibold text-white mt-4 mb-2">6.1 User-Created Content</h3>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Users retain ownership of their created games</li>
                  <li>Users grant TriviaPlug a license to host and display their games on the Platform</li>
                  <li>Users are responsible for ensuring their content does not violate any laws or third-party rights</li>
                </ul>

                <h3 className="text-xl font-semibold text-white mt-4 mb-2">6.2 Prohibited Content</h3>
                <p>Games may not contain:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Copyrighted material without permission</li>
                  <li>Defamatory, offensive, or illegal content</li>
                  <li>Personal information of others without consent</li>
                  <li>Content that violates intellectual property rights</li>
                </ul>

                <h3 className="text-xl font-semibold text-white mt-4 mb-2">6.3 Marketplace Approval</h3>
                <p>
                  Games submitted to the marketplace are subject to review and approval. TriviaPlug reserves the right to reject any game at our discretion.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">7. Stripe Connect</h2>
                <p>
                  Creators who wish to receive payments from marketplace sales must set up a Stripe Connect account. By connecting your account, you agree to:
                </p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Stripe's Terms of Service</li>
                  <li>Providing accurate information for tax purposes</li>
                  <li>Receiving 1099-K forms if applicable</li>
                  <li>Compliance with all applicable payment processing regulations</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">8. Intellectual Property</h2>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>TriviaPlug's Platform, trademarks, and proprietary content are owned by TriviaPlug LLC</li>
                  <li>Users retain ownership of their game content</li>
                  <li>Users grant TriviaPlug a license to use, display, and distribute their games on the Platform</li>
                  <li>Unauthorized copying or distribution of Platform content is prohibited</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">9. Limitation of Liability</h2>
                <p>
                  TriviaPlug shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the Platform. Our total liability for any claims shall not exceed the amount you paid us in the 12 months prior to the claim.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">10. Service Modifications</h2>
                <p>
                  TriviaPlug reserves the right to modify, suspend, or discontinue any part of the Platform at any time, with or without notice. We are not liable to you or any third party for any modification, suspension, or discontinuation.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">11. Termination</h2>
                <p>
                  We may terminate or suspend your account immediately, without prior notice, if you breach these Terms. Upon termination, your right to use the Platform will cease immediately.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">12. Dispute Resolution</h2>
                <p>
                  Any disputes arising from these Terms or your use of the Platform shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">13. Changes to Terms</h2>
                <p>
                  TriviaPlug reserves the right to update these Terms at any time. We will notify users of material changes via email or Platform notification. Continued use of the Platform after changes constitutes acceptance of the new Terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">14. Contact Information</h2>
                <p>
                  If you have questions about these Terms, please contact us at:
                </p>
                <ul className="list-none ml-4 space-y-2">
                  <li>Email: support@triviaplug.com</li>
                  <li>Support Tickets: Available through the Help Desk page</li>
                </ul>
              </section>

            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-700">
            <p className="text-gray-400 text-sm">
              By using TriviaPlug, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;

